/* eslint-disable no-bitwise */
import type { HandType } from '@ps/web-poker-types';
import { AwardScheme, TournFlags, TournFlags2, TournFlagsServInt } from '@ps/web-poker-types';
import { is64BitFlagSet } from '@ps/web-poker-utils';
import BitSet from 'bitset';
import { compareVersions } from 'compare-versions';

import pkg from '../../../package.json';
import { GameCategoryTypes, legacyHandTypeToAssetNameMap } from '../types';

import { TournGroupTypes } from './tournGroups';
import type { Tag } from './types';
import { FlagsetName, TableFlags2, TimeToStopMatchType } from './types';

// https://github.com/FlutterInt/fips-shared-protocols/blob/master/gamedefs.h
const MultiGameTypeShift = 100;

export const GameCategoryTypeToLabel = {
  [GameCategoryTypes.MTT]: 'tourns',
  [GameCategoryTypes.Cash]: 'tableTypes',
  [GameCategoryTypes.SNG]: 'sngTypes',
  [GameCategoryTypes.SAG]: 'sagTypes',
  [GameCategoryTypes.Zoom]: 'zoomTypes',
  [GameCategoryTypes.SFN]: 'seatFinders',
  [GameCategoryTypes.PPP]: 'pppTypes'
};

const TournTags: Record<number, string> = {
  [TournGroupTypes.TournGroup_Knockouts]: 'KO',
  [TournGroupTypes.TournGroup_Phased]: 'PHASE',
  [TournGroupTypes.TournGroup_Sunday_Zoom]: 'ZOOM',
  [TournGroupTypes.TournGroup_TimeTourneys]: 'TIME',
  [TournGroupTypes.TournGroup_Fifty50]: 'FIFTY50'
  // 'On Demand': 'ON-DEMAND'
};

const flagChecks: {
  [key: string]: Record<number, string>;
} = {
  TournFlagsServInt: {
    [TournFlagsServInt.TOURNFLAG_SERVINT_BLITZ]: 'ZOOM'
  },
  TournFlags: {
    [TournFlags.TOURNSCRIPT_FLAG_REBUYENABLE]: 'REBUY'
  },
  TournFlags2: {
    [TournFlags2.TOURNSCRIPT_FLAG2_NSTACK]: 'N-STACK',
    [TournFlags2.TOURNSCRIPT_FLAG2_KNOCKOUT]: 'KO',
    [TournFlags2.TOURNSCRIPT_FLAG2_WIN_THE_BUTTON]: 'WIN THE BUTTON',
    [TournFlags2.TOURNSCRIPT_FLAG2_BUTTON_BLIND]: 'BB',
    [TournFlags2.TOURNSCRIPT_FLAG2_FLIGHT_ENTRY]: 'PHASE',
    [TournFlags2.TOURNSCRIPT_FLAG2_FLIGHT_INTERMEDIATE]: 'PHASE',
    [TournFlags2.TOURNSCRIPT_FLAG2_FLIGHT_FINAL]: 'PHASE'
  }
};

const getVariantFlagTags = (flagset: keyof typeof FlagsetName, mask?: number | string): Tag[] => {
  if (!mask) return [];

  const flagsToCheck = flagChecks[flagset];
  const getTag = (title: string) => ({ color: 'red', title });
  const hexMask = Number(mask).toString(16);

  return Object.keys(flagsToCheck).reduce((acc: Tag[], flag) => {
    const bit = Math.log2(Number(flag));
    if (flagset === FlagsetName.TournFlags2 && hexMask !== '0') {
      const flagMask = BitSet.fromHexString(hexMask);
      if (flagMask.get(bit)) {
        acc.push(getTag(flagsToCheck[Number(flag)]));
      }
    } else if (Number(flag) & Number(mask)) {
      acc.push(getTag(flagsToCheck[Number(flag)]));
    }
    return acc;
  }, []);
};

export enum Structure {
  'NonDefined' = 0,
  'L' = 1,
  'PL' = 2,
  'NL' = 3,
  'PNL' = 4
}

export enum Speed {
  'Regular' = 1,
  'Turbo' = 2,
  'Hyper' = 3,
  'Slow' = 4
}
const ASSETS_VERSIONING_START = '0.0.1';
const THE_RAIL_CDN_URL = 'https://thehub.rationalcdn.com/pokerTileImages/assets/';
const THEME_V2_SUBFOLDER = 'themeV2/';
const THE_RAIL_CDN_URL_THEME_V2 = `${THE_RAIL_CDN_URL}${THEME_V2_SUBFOLDER}`;

// If consumer version is smaller than the version when we started assets versioning fallback to the default CDN
const appendPackageVersion = (url: string) =>
  compareVersions(pkg.version, ASSETS_VERSIONING_START) === -1 ? url : `${url}v${pkg.version}/`;

export const THE_RAIL_CDN_BASE_URL = appendPackageVersion(THE_RAIL_CDN_URL);
export const THE_RAIL_CDN_BASE_URL_THEME_V2 = appendPackageVersion(THE_RAIL_CDN_URL_THEME_V2);

export const getRailCdnBaseUrl = (isThemeV2Image?: boolean) => {
  const url = isThemeV2Image ? THE_RAIL_CDN_URL_THEME_V2 : THE_RAIL_CDN_URL;
  return appendPackageVersion(url);
};

export const NGCDN_BASE_URL = 'https://csapp.rationalcdn.com/pokerstars/ios/public/tiles/';
export const NG_CDN_IMAGE_SUFFIX = '_square'; // POKER-12433
export const NG_CDN_WIDE_IMAGE_SUFFIX = '_wide'; // POKER-13849
export const TILE_IMAGE_FORMAT = '.jpg';
export const ICON_IMAGE_FORMAT = '.png';
export const NG_IMAGE_SUFFIX = `${NG_CDN_IMAGE_SUFFIX}${TILE_IMAGE_FORMAT}`;
export const NG_WIDE_IMAGE_SUFFIX = `${NG_CDN_WIDE_IMAGE_SUFFIX}${TILE_IMAGE_FORMAT}`;
export const BASE_POKER_STR = 'pkr_';
export const SAG_PROMO_SUFFIX = '_sag';
export const BUY_IN_RANGES = ['micro', 15000, 'low', 50000, 'medium', 400000, 'high'];
export const BUY_IN_RANGES_SNG = ['micro', 5000, 'low', 20000, 'medium', 100000, 'high'];

export const formatPlayers = (maxPlayers: number) => {
  if (maxPlayers === 2) {
    return 'HEADS UP';
  }
  return `${maxPlayers}-MAX`;
};

export const formatGame = (
  game: HandType,
  structure: number,
  isHiLo: boolean,
  ignoreStructure: boolean
) => {
  let gameStr = legacyHandTypeToAssetNameMap[game];
  let structureStr = Structure[structure];

  if (gameStr === undefined) {
    throw new Error(`game ${game} not supported`);
  }

  if (structureStr === undefined) {
    throw new Error(`structure ${structure} not supported`);
  }

  if (structureStr === 'L') structureStr = 'Limit';
  if (isHiLo) gameStr += ' HL';

  gameStr = gameStr.replace(/holdem/gi, "Hold'em");
  return `${!ignoreStructure ? structureStr : ''} ${gameStr}`;
};

const isShootout = (tournFlags?: number, minPlayers?: number) =>
  tournFlags &&
  tournFlags & TournFlags.TOURNSCRIPT_FLAG_OLYMPICHACK &&
  (minPlayers !== 2 || !(tournFlags & TournFlags.TOURNSCRIPT_FLAG_SITANDGO));

const isFlighted = (tournFlags2: number) =>
  is64BitFlagSet(tournFlags2, TournFlags2.TOURNSCRIPT_FLAG2_FLIGHT_ENTRY) ||
  is64BitFlagSet(tournFlags2, TournFlags2.TOURNSCRIPT_FLAG2_FLIGHT_INTERMEDIATE) ||
  is64BitFlagSet(tournFlags2, TournFlags2.TOURNSCRIPT_FLAG2_FLIGHT_FINAL);

const isTimed = (tournFlags2: number, timedTournType?: number, timedTournPlayersPercent?: number) =>
  is64BitFlagSet(tournFlags2, TournFlags2.TOURNSCRIPT_FLAG2_BEAT_THE_CLOCK) ||
  timedTournType !== TimeToStopMatchType.eTimeToStopMatchTypeNone ||
  (timedTournPlayersPercent && timedTournPlayersPercent > 0 && !isFlighted(tournFlags2));

const isFifty50 = (
  tournFlags2: number,
  timedTournType?: number,
  timedTournPlayersPercent?: number,
  awardScheme?: number
) =>
  !isTimed(tournFlags2, timedTournType, timedTournPlayersPercent) &&
  awardScheme === AwardScheme.PRIZE_CHIPS_PROPORTIONAL;

export const isMixedGame = (game: number) =>
  game >= MultiGameTypeShift && game <= MultiGameTypeShift + 8;

export interface Tags {
  game: number;
  structure: number;
  minPlayers?: number;
  maxPlayers?: number;
  maxPerTable?: number;
  numReentries?: number;
  ante?: number;
  cap?: number;
  tournGroup?: TournGroupTypes;
  satelliteTarget?: number;
  tournFlags?: number;
  speedDisplay?: number;
  tournFlagsServInt?: number;
  tableFlags2?: number;
  tournFlags2?: string;
  isMTT?: boolean;
  awardScheme?: number;
  timedTournType?: number;
  timedTournPlayersPercent?: number;
  isHiLo?: boolean;
}

interface CheckTagParams {
  tournGroup: number;
  tournFlags: number;
  tournFlags2: number;
  tournFlagsServInt: number;
}

export const isTagAvailable = (
  tag: string,
  { tournGroup, tournFlags, tournFlags2, tournFlagsServInt }: CheckTagParams
): boolean => {
  const tournGroupTag = tournGroup ? TournTags[tournGroup] : null;
  const tournFlagsTag = tournFlags ? TournTags[tournFlags] : null;
  const tournFlags2Tag = tournFlags2 ? TournTags[tournFlags2] : null;
  const tournTag = tournGroupTag || tournFlagsTag || tournFlags2Tag;

  const tags = [
    ...getVariantFlagTags('TournFlagsServInt', Number(tournFlagsServInt)).map((tag) => tag.title),
    ...getVariantFlagTags('TournFlags', Number(tournFlags)).map((tag) => tag.title),
    ...getVariantFlagTags('TournFlags2', Number(tournFlags2)).map((tag) => tag.title)
  ];

  return tournTag === tag || tags.includes(tag);
};

export const isZoom = (options: CheckTagParams): boolean => isTagAvailable('ZOOM', options);

export const isKnockout = (options: CheckTagParams): boolean => isTagAvailable('KO', options);

export enum KnockoutType {
  'KO' = 'KO',
  'PKO' = 'PKO',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'Total_PKO' = 'Total_PKO',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'Ultra_PKO' = 'Ultra_PKO'
}

export const getKnockoutType = (buyIn: number, bounty: number): KnockoutType => {
  const percentage = (bounty / buyIn) * 100;

  if (percentage === 100) {
    return KnockoutType.Total_PKO;
  }
  if (percentage >= 80 && percentage < 100) {
    return KnockoutType.Ultra_PKO;
  }
  if (percentage >= 50 && percentage < 80) {
    return KnockoutType.PKO;
  }
  return KnockoutType.KO;
};

export default function gameFormatter({
  game,
  structure,
  minPlayers,
  maxPlayers,
  maxPerTable,
  numReentries,
  ante,
  cap,
  tournGroup,
  satelliteTarget,
  tournFlags,
  speedDisplay,
  tournFlagsServInt,
  tableFlags2,
  tournFlags2,
  isMTT,
  awardScheme,
  timedTournType,
  timedTournPlayersPercent,
  isHiLo
}: Tags): Tag[] {
  // @ts-ignore TODO: fix types
  const tournTag = TournTags[tournGroup] || TournTags[tournFlags] || TournTags[tournFlags2];
  const ignoreStructure = isMixedGame(game);
  const tags = [{ title: formatGame(game, structure, !!isHiLo, ignoreStructure), color: 'blue' }];

  if (maxPerTable && maxPerTable !== 9) {
    tags.push({ title: formatPlayers(maxPerTable), color: 'purple' });
  }

  if (speedDisplay && speedDisplay !== Speed.Regular) {
    tags.push({ title: Speed[speedDisplay], color: 'light-grey' });
  }

  if (ante && ante !== 0) {
    tags.push({ title: 'ANTE', color: 'red' });
  }

  if (tableFlags2 && tableFlags2 & TableFlags2.TABLESCRIPT_FLAG2_BUTTON_BLIND) {
    tags.push({ title: 'BB', color: 'red' });
  }

  if (cap && cap !== 0) {
    tags.push({ title: 'CAP', color: 'red' });
  }

  // On-Demand SnG - AQA-31681
  if (
    isMTT &&
    tournFlags &&
    tournFlags & TournFlags.TOURNSCRIPT_FLAG_SITANDGO &&
    minPlayers !== maxPlayers
  ) {
    tags.push({ title: 'ON-DEMAND', color: 'red' });
  }

  if (numReentries && numReentries !== 0) {
    tags.push({
      title: numReentries !== -1 ? `${numReentries}x RE-ENTRY` : '∞ RE-ENTRY',
      color: 'red'
    });
  }

  tags.push(
    ...getVariantFlagTags('TournFlagsServInt', tournFlagsServInt),
    ...getVariantFlagTags('TournFlags', tournFlags),
    ...getVariantFlagTags('TournFlags2', tournFlags2)
  );

  if (isShootout(tournFlags, minPlayers)) {
    tags.push({ title: 'SHOOTOUT', color: 'red' });
  }

  if (
    tournFlags2 &&
    isFifty50(Number(tournFlags2), timedTournType, timedTournPlayersPercent, awardScheme)
  ) {
    tags.push({ title: 'FIFTY50', color: 'red' });
  }

  if (tournFlags2 && isTimed(Number(tournFlags2), timedTournType, timedTournPlayersPercent)) {
    tags.push({ title: 'TIME', color: 'red' });
  }

  if (tournTag) {
    tags.push({
      title: tournTag,
      color: 'red'
    });
  }

  if (satelliteTarget && satelliteTarget !== 0) {
    tags.push({ title: 'SAT', color: 'blue' });
  }

  const arrayUniqueByKey = (array: Tag[], key: keyof Tag): Tag[] => [
    ...new Map<string, Tag>(array.map((item) => [item[key], item])).values()
  ];

  return arrayUniqueByKey(tags, 'title');
}
